import React, { useEffect, useState } from 'react';
import axios from 'axios';
import cn from 'classnames';
import { omit } from 'lodash';
import { IClickData, sendClickEvent } from '@fiverr-private/homepage_utils';
import { Container } from '@fiverr-private/layout_components';
import { JobsTBDTypes } from '@fiverr-private/homepage_common_types';
import { JobBasicData } from '@fiverr-private/jtbd_sdk';
import { Box } from '@fiverr-private/theme';
import { getContext } from '@fiverr-private/fiverr_context';
import { mixpanel, logger } from '@fiverr-private/obs';
import { Carousel, Track, Slide, Arrows } from '@fiverr-private/carousel';
import { JobTbdCard } from '../../JobTbdCard/JobTbdCard';
import { JobTbdCardSkeleton } from '../../JobTbdCardSkeleton/JobTbdCardSkeleton';
import { isJobWithFullData } from '../../../../lib/interfaces/jobsTBD';
import { sendGrafanaMetric } from '../../JobTbdCard/JobTbdCard.utils';
import { useJobsData } from '../JobsTbdContainer.hooks';
import { JobsProps } from '../JobsTbdContainer.types';
import { JTBD_CONTAINER_WIDTH } from '../constants';
import {
    halfWidthSlide,
    animate,
    dismissedSlidesStyle,
    twoSlidesStyle,
    fadeOut,
    opacityOnlyTransition,
    hidden,
    commonStyles,
    mobileSlidesStyle,
} from '../JobsTbdContainer.ve.css';

export const Jobs = (props: JobsProps) => {
    const { jobsFullData } = props;
    const { isTouch } = getContext();
    const initialJobsToDisplay = jobsFullData.slice(0, 3);
    const [jobsToDisplay, setJobsToDisplay] = useState<string[]>(initialJobsToDisplay.map((job) => job.jobType));
    const [dismissedJobs, setDismissedJobs] = useState<string[]>([]);
    const [scrollToIndex, setScrollToIndex] = useState(-1);
    const jobsCount = jobsToDisplay.length;
    const numOfDismissedJobs = dismissedJobs.length;
    const lastDismissedJob = dismissedJobs[numOfDismissedJobs - 1];
    const indexOfLastDismissedJob = initialJobsToDisplay.findIndex((job) => job.jobType === lastDismissedJob);

    useEffect(() => {
        mixpanel.track('Home - JTBD section Impression', {
            'Number of Cards': jobsCount,
            'JTBD Card Names': jobsToDisplay,
        });

        // we want to report this only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const jobCardMaxWidth = JTBD_CONTAINER_WIDTH / jobsToDisplay.length;

    const sendDismissMetrics = (type: JobsTBDTypes, dismissedJobPosition: number, impId: string) => {
        const clickData: IClickData = {
            imp: {
                id: impId,
            },
            page_element_name: `dismissed_jtbd_${type}`,
            clicked_tab_data: {
                tab_position: dismissedJobPosition + 1,
                tab_asset: '',
                tab_logic: '',
            },
        };

        sendClickEvent(clickData);
        sendGrafanaMetric(type, 'dismiss');
        mixpanel.track(`Home - JTBD Dismiss`, {
            cardName: type,
            cardPosition: dismissedJobPosition + 1,
            shownCards: jobsToDisplay,
        });
    };

    // Dismiss card logic

    const onDismiss = async (
        e: React.MouseEvent<HTMLButtonElement>,
        type: JobsTBDTypes,
        dismissInfo: JobBasicData['dismissInfo'],
        impId: string
    ) => {
        try {
            e.preventDefault();
            e.stopPropagation();
            const dismissedJobPosition = initialJobsToDisplay.findIndex((job) => job.jobType === type);

            sendDismissMetrics(type, dismissedJobPosition, impId);

            if (dismissedJobPosition === initialJobsToDisplay.length - 1) {
                setScrollToIndex(dismissedJobPosition - 1);
            } else setTimeout(() => setScrollToIndex(dismissedJobPosition), 800);
            setTimeout(() => setScrollToIndex(-1), 900);

            setDismissedJobs((prev) => [...prev, type]);

            setTimeout(() => setJobsToDisplay((prevJtbds) => prevJtbds.filter((job) => job !== type)), 1000);
            await axios.post('/tailored_homepage/dismiss_jtbd', {
                jtbdType: type,
                ...omit(dismissInfo, 'isDismissible'),
            });
        } catch (error: any) {
            logger.error(error, { description: `Failed to dismiss card: ${type}` });
        }
    };

    const getSlideClassname = (jobType: string) => {
        const classes = { [commonStyles]: true, [mobileSlidesStyle]: isTouch };

        classes[hidden] = jobsToDisplay.findIndex((job) => jobType === job) < 0;

        if (!numOfDismissedJobs) {
            return initialJobsToDisplay.length === 3 ? cn(dismissedSlidesStyle, classes) : cn(halfWidthSlide, classes);
        }

        classes[fadeOut] = jobType === lastDismissedJob;

        classes[animate] = jobType === jobsToDisplay[indexOfLastDismissedJob + 1];

        classes[opacityOnlyTransition] = jobsToDisplay.length < initialJobsToDisplay.length;

        switch (initialJobsToDisplay.length) {
            case 3:
                return cn(dismissedSlidesStyle, classes);
            case 2:
                return cn(twoSlidesStyle, classes);
            default:
                return cn(halfWidthSlide, classes);
        }
    };

    const JobsContainer = initialJobsToDisplay.map((jobToDisplay) => (
        <Slide key={jobToDisplay.jobType} className={getSlideClassname(jobToDisplay.jobType)} aria-label="Slide">
            <JobTbdElement
                {...jobToDisplay}
                jobMaxWidth={jobCardMaxWidth}
                jobsCount={jobsCount}
                onDismiss={onDismiss}
                withCta={initialJobsToDisplay.length < 3}
            />
        </Slide>
    ));

    return (
        <Box width="100%" maxWidth={{ lg: '1400px' }} margin="auto" data-testid="jobs-tbd-container">
            <Carousel
                width={{ default: '98%', lg: '100%' }}
                breakpoints={isTouch ? { default: 1, sm: 2 } : { default: 2, lg: 3 }}
                scrollToSlideIndex={scrollToIndex > -1 ? scrollToIndex : undefined}
                paddingLeft={{ default: '5', sm: '0' }}
            >
                <Container display={isTouch ? 'none' : { default: 'block', lg: 'none' }}>
                    <Arrows size="sm" />
                </Container>
                <Track>{JobsContainer}</Track>
            </Carousel>
        </Box>
    );
};

const JobTbdElement = (
    jobToDisplay: ReturnType<typeof useJobsData>[number] & {
        jobMaxWidth: number;
        jobsCount: number;
        onDismiss: (e: any, type: JobsTBDTypes, dismissInfo: JobBasicData['dismissInfo'], impId: string) => void;
        withCta: boolean;
    }
) =>
    isJobWithFullData(jobToDisplay) ? (
        <Container width="100%" marginBottom={{ default: '0', lg: '6' }} aria-label="Slide">
            <JobTbdCard {...jobToDisplay} onDismiss={jobToDisplay.onDismiss} withCta={jobToDisplay.withCta} />
        </Container>
    ) : (
        <Container width="100%">
            <JobTbdCardSkeleton jobMaxWidth={jobToDisplay.jobMaxWidth} />
        </Container>
    );
